import {useCallback, useContext, useEffect, useMemo} from "react";
import {useNavigate} from "react-router-dom";

import CloseButton from "common/components/CloseButton";
import {ResponsiveHeader, ResponsiveHeaderType} from "common/components/header/ResponsiveHeader";
import LoadingSpinner from "common/components/spinner";
import {EventName} from "common/constants/events";
import {useAbsolutePath} from "common/hooks/useAbsolutePath";
import {FetchStatus, useFetch} from "common/hooks/useFetch";
import {useI18n} from "common/hooks/useI18n";
import {useTracking} from "common/hooks/useTracking";
import {Api} from "common/services/apiProvider";
import {getDocumentTitle} from "common/util/DocumentTitleUtil";
import {AccountContextProvider} from "features/account/accountStateProvider";
import {
    AddVehiclePageContextProvider,
    AddVehiclePageProvider,
} from "features/companies/vehicles/list-view/components/AddVehiclePageProvider";

import {VehicleMarketplaceService} from "@bolteu/bolt-server-api-fleet-owner-portal";
import {FormState, useForm} from "@bolteu/kalep-form-react";
import {Grid, SelectOption} from "@bolteu/kalep-react";
import {Plus} from "@bolteu/kalep-react-icons";

interface VehicleListingCreateForm {
    model_id?: number;
}

const getVehicleModelsFetchFunction = (api: Api) => api.fleetOwnerPortal.vehicleModelsList({});
const addVehicleListingFunction = (api: Api, model: VehicleMarketplaceService.CreateListingRequest) =>
    api.vehicleMarketplace.vehicleListingCreate(model);

export interface AddVehicleListingPageProps {
    closePage: () => void;
    onListingAdded: (carApplication: VehicleMarketplaceService.CreateListingResponse) => Promise<void>;
}

const AddVehicleListingPage = () => {
    const {isAddVehiclePageOpen, openAddVehiclePage} = useContext(AddVehiclePageContextProvider);
    const {getVehiclesPath, getVehicleListingEditPath} = useAbsolutePath();
    const {i18n} = useI18n();
    const {trackEvent} = useTracking();

    useEffect(() => {
        const pageTitle = i18n("auth.app.fleet.vehicle_marketplace.listings.title");
        document.title = getDocumentTitle(pageTitle);
    }, [i18n]);

    const navigate = useNavigate();
    const accountState = useContext(AccountContextProvider);
    const fleet = accountState?.getFleet();
    const {
        data: vehicleModelsData,
        fetch: fetchVehicleModels,
        status: fetchVehicleModelsStatus,
    } = useFetch(getVehicleModelsFetchFunction);

    const {
        fetch: fetchAddNewListing,
        status: addNewListingStatus,
        data: addNewListingData,
    } = useFetch(addVehicleListingFunction);

    const isSubmitting = [addNewListingStatus].includes(FetchStatus.Loading);

    useEffect(() => {
        if (fetchVehicleModels && fleet) {
            fetchVehicleModels({});
        }
    }, [fetchVehicleModels, fleet]);

    const onClosePageClick = useCallback(() => {
        navigate(getVehiclesPath());
    }, [navigate, getVehiclesPath]);

    const defaultFormValues: VehicleListingCreateForm = {};

    const {Form, AutocompleteField, Button} = useForm(defaultFormValues);

    const onSubmitForm = useCallback(
        async (state: FormState<VehicleListingCreateForm>) => {
            if (fetchAddNewListing) {
                fetchAddNewListing({
                    vehicle_model_id: state.data.model_id || 0,
                });
            }
        },
        [fetchAddNewListing],
    );

    const vehicleModels = useMemo(
        () =>
            (vehicleModelsData?.list ?? [])
                .sort((a, b) => b.total_number - a.total_number)
                .map(
                    (model) =>
                        ({
                            value: model.model_id,
                            title: model.model,
                            secondary: i18n("auth.app.fleet.vehicle_marketplace.listings.add.amount_of_models", {
                                total: model.total_number,
                            }),
                        } as SelectOption),
                ),
        [vehicleModelsData, i18n],
    );

    useEffect(() => {
        if (addNewListingStatus === FetchStatus.Success) {
            trackEvent(EventName.AddNewVehicleListingSubmitSuccessful);
            const createdListing = addNewListingData.listing_id;
            const path = getVehicleListingEditPath(createdListing);
            navigate(path);
        } else if (addNewListingStatus === FetchStatus.Error) {
            trackEvent(EventName.AddNewVehicleListingSubmitFailed);
        }
    }, [trackEvent, addNewListingStatus, addNewListingData, navigate, getVehicleListingEditPath]);

    if (fetchVehicleModelsStatus === FetchStatus.Loading) {
        return <LoadingSpinner show size="lg" showLoadingText parentClassName="mt-9" />;
    }

    if (isAddVehiclePageOpen) {
        return null;
    }

    return (
        <Form onSubmit={onSubmitForm}>
            <div className="m-auto my-10 flex w-[484px] max-w-3xl flex-col justify-center">
                <div className="flex justify-end">
                    <CloseButton onClick={onClosePageClick} />
                </div>
                <div className="my-6 flex flex-col gap-3">
                    <ResponsiveHeader
                        type={ResponsiveHeaderType.InnerPage}
                        text={i18n("auth.app.fleet.vehicle_marketplace.listings.add.title")}
                    />
                </div>
                <p>{i18n("auth.app.fleet.vehicle_marketplace.listings.add.description")}</p>
                <p className="my-2">
                    <Button variant="tertiary" startIcon={<Plus />} onClick={openAddVehiclePage}>
                        {i18n("auth.app.fleet.add-vehicle.title")}
                    </Button>
                </p>
                <Grid columns={1}>
                    <AutocompleteField
                        name="model_id"
                        label={i18n("auth.app.fleet.vehicle_marketplace.listings.add.select_model_title")}
                        placeholder={i18n("auth.app.fleet.vehicle_marketplace.listings.add.select_model")}
                        options={vehicleModels}
                        required
                    />
                </Grid>
                <div className="mt-6">
                    <Button submit variant="primary" loading={isSubmitting} disabled={isSubmitting}>
                        {i18n("form.button.next")}
                    </Button>
                </div>
            </div>
        </Form>
    );
};

const AddVehicleListingPageWithProvider = () => (
    <AddVehiclePageProvider>
        <AddVehicleListingPage />
    </AddVehiclePageProvider>
);

export default AddVehicleListingPageWithProvider;
